import { useCallback, useEffect, useRef, useState, type DependencyList } from 'react';
import { ApiError } from '../api';

// Одно поведение загрузки на все разделы: пока идёт — «загружаю», отказ —
// строка ошибки, просроченный токен — выход на экран входа. Повторять это в
// каждом разделе значит однажды забыть про 401 в одном из них и оставить
// человека перед пустой таблицей вместо формы входа.

export interface Resource<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  /**
   * Перечитать с сервера то же самое (issue #524).
   *
   * Нужно там, где панель сама что-то записала: после ввода допуска карточка
   * работника и его строка в списке обязаны показать новое состояние, и
   * посчитать его в браузере нельзя — допущен человек или нет, решает сервер.
   *
   * ПРЕЖНЕЕ ОСТАЁТСЯ НА ЭКРАНЕ, пока идёт перечитывание: оно ещё верно, а
   * мигание пустотой закрыло бы открытую карточку работника. С чистого листа
   * загрузка начинается только когда меняются входные данные — другая
   * организация или другой работник.
   */
  reload: () => void;
}

export function useResource<T>(
  load: () => Promise<T>,
  onUnauthorized: () => void,
  deps: DependencyList,
): Resource<T> {
  const [state, setState] = useState<{ data: T | null; error: string | null; loading: boolean }>({
    data: null,
    error: null,
    loading: true,
  });
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((previous) => previous + 1), []);
  // Версия, с которой эффект отработал в прошлый раз. Совпала — значит эффект
  // запустили изменившиеся deps, а не `reload()`.
  const lastVersion = useRef(version);

  useEffect(() => {
    const refetch = lastVersion.current !== version;
    lastVersion.current = version;
    let alive = true;
    setState((previous) => ({ data: refetch ? previous.data : null, error: null, loading: true }));
    load().then(
      (data) => {
        if (alive) setState({ data, error: null, loading: false });
      },
      (error: unknown) => {
        if (!alive) return;
        if (error instanceof ApiError && error.status === 401) onUnauthorized();
        setState({
          data: null,
          error: error instanceof Error ? error.message : 'Не удалось загрузить данные',
          loading: false,
        });
      },
    );
    return () => {
      alive = false;
    };
  }, [...deps, version]);

  return { ...state, reload };
}
